import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { AccessMode } from "../lib/types";
import { StatusBadge, Badge } from "../components/StatusBadge";
import { Table, THead, TH, TBody, TR, TD } from "../components/Table";
import { Loading, ErrorState } from "../components/EmptyState";
import { OwnerCell } from "../components/OwnerCell";
import { formatBytes, formatDate } from "../lib/format";
import { useMe } from "../lib/hooks";
import { AddDestinationModal } from "./destinations";

const STATUS_TONE = { active: "ok", pending: "warn", error: "danger" } as const;

const ACCESS_LABEL: Record<AccessMode, string> = {
  org: "Everyone",
  admins: "Admins only",
  members: "Select members",
};

export function ConnectorDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const me = useMe();
  const canManage =
    me.data?.org.role === "owner" || me.data?.org.role === "admin";
  const [adding, setAdding] = useState(false);

  const connector = useQuery({
    queryKey: ["connector", id],
    queryFn: () => api.connectors.get(id),
  });
  const destinations = useQuery({
    queryKey: ["destinations"],
    queryFn: () => api.destinations.list(),
  });

  const test = useMutation({
    mutationFn: () => api.connectors.test(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["connector", id] });
      qc.invalidateQueries({ queryKey: ["connectors"] });
      qc.invalidateQueries({ queryKey: ["destinations"] });
    },
  });
  const remove = useMutation({
    mutationFn: () => api.connectors.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["connectors"] });
      navigate("/connectors");
    },
  });

  // Self-heal on view: no background job revisits a still-settling connector, so
  // opening one that hasn't confirmed access yet quietly re-probes once. A grant
  // that finished propagating after the wizard closed then flips to Healthy on
  // its own, with no manual "Test access" click. Gated to the reachable settling
  // states (health 'unchecked' — an inconclusive probe writes that, never
  // 'pending' — or a still-pending connector) AND to connectors that actually
  // have a destination, since the probe 409s ("add a destination first")
  // otherwise and we don't want that shown unprompted on load.
  const autoProbed = useRef(false);
  useEffect(() => {
    const data = connector.data;
    if (!data || autoProbed.current) return;
    const settling = data.health === "unchecked" || data.status === "pending";
    const hasDestination = (destinations.data ?? []).some(
      (d) => d.connectorId === id,
    );
    if (settling && hasDestination && test.isIdle) {
      autoProbed.current = true;
      test.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connector.data, destinations.data]);

  if (connector.isLoading) return <Loading />;
  if (connector.isError || !connector.data)
    return <ErrorState error={connector.error} onRetry={connector.refetch} />;

  const c = connector.data;
  const children = (destinations.data ?? []).filter((d) => d.connectorId === id);
  const testOutcome =
    test.isSuccess && test.data
      ? {
          ok: test.data.ok,
          pending: test.data.health === "pending",
          title: test.data.diagnosis.title,
          nextStep: test.data.diagnosis.nextStep,
        }
      : test.isError
        ? {
            ok: false,
            pending: false,
            title: test.error instanceof Error ? test.error.message : "Test failed.",
          }
        : undefined;

  return (
    <div className="space-y-5">
      <div>
        <div className="mb-1 text-sm text-ink-subtle">
          <button onClick={() => navigate("/connectors")} className="hover:text-ink">
            Connections
          </button>{" "}
          › <span className="text-ink">{c.displayName}</span>
        </div>
        <div className="flex items-start justify-between gap-4">
          <NameEditor
            id={id}
            name={c.displayName}
            editable={canManage}
            onSaved={() => connector.refetch()}
          />
          <div className="flex shrink-0 items-center gap-2">
            <button
              className="btn-secondary"
              onClick={() => test.mutate()}
              disabled={test.isPending}
            >
              {test.isPending ? "Testing…" : "Test access"}
            </button>
          </div>
        </div>
        {testOutcome && (
          <div
            className={`mt-2 rounded-md border px-3 py-2 text-sm ${
              testOutcome.ok
                ? "border-ok/30 bg-ok-soft text-ok"
                : testOutcome.pending
                  ? "border-surface-200 bg-surface-100 text-ink-muted"
                  : "border-warn/30 bg-warn-soft text-warn"
            }`}
          >
            {testOutcome.ok ? "✓ " : testOutcome.pending ? "◌ " : "✗ "}
            {testOutcome.title}
            {testOutcome.nextStep && (
              <span className="mt-0.5 block text-xs opacity-90">
                {testOutcome.nextStep}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Configuration */}
      <div className="card p-5">
        <h2 className="mb-3 text-sm font-semibold text-ink">Configuration</h2>
        <dl className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
          <Field label="Cloud" value={c.cloud === "aws" ? "Amazon Web Services" : "Microsoft Azure"} />
          <Field label="State" value={<Badge tone={STATUS_TONE[c.status]}>{c.status}</Badge>} />
          <Field label="Health" value={<StatusBadge health={c.health} />} />
          <Field label="Created" value={formatDate(c.createdAt)} />
          {c.cloud === "aws" ? (
            <>
              <Field label="Role ARN" mono value={c.awsRoleArn ?? "—"} />
              <Field label="Region" value={c.awsRegion ?? "—"} />
              <Field
                label="External ID"
                value={c.awsExternalIdSet ? "Set" : "Not set"}
              />
            </>
          ) : (
            <>
              <Field label="Storage account" mono value={c.azStorageAccount ?? "—"} />
              <Field label="Tenant" mono value={c.azTenantId ?? "—"} />
            </>
          )}
        </dl>
      </div>

      {/* Access — who can see + use this connection */}
      {canManage ? (
        <AccessCard connectorId={id} accessMode={c.accessMode} />
      ) : null}

      {/* Destinations under this connection */}
      <div className="card p-5">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-ink">
            Destinations <span className="text-ink-subtle">({children.length})</span>
          </h2>
          {canManage && (
            <button className="btn-secondary" onClick={() => setAdding(true)}>
              Add destination
            </button>
          )}
        </div>
        {children.length > 0 ? (
          <Table>
            <THead>
              <tr>
                <TH>Bucket / container</TH>
                <TH>Prefix</TH>
                <TH>Max size</TH>
                <TH>Status</TH>
              </tr>
            </THead>
            <TBody>
              {children.map((d) => (
                <TR key={d.id} onClick={() => navigate(`/destinations/${d.id}`)}>
                  <TD className="mono font-medium text-ink">{d.bucketOrContainer}</TD>
                  <TD className="mono text-xs text-ink-muted">{d.keyPrefix || "—"}</TD>
                  <TD className="text-ink-muted">{formatBytes(d.maxBytes)}</TD>
                  <TD>
                    <StatusBadge health={d.status} />
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        ) : (
          <p className="text-sm text-ink-muted">
            No destinations yet. Add one to start issuing upload keys.
          </p>
        )}
      </div>

      {/* Danger zone */}
      {canManage && (
        <div className="card border-danger/30 p-5">
          <h2 className="text-sm font-semibold text-ink">Danger zone</h2>
          <div className="mt-2 flex items-center justify-between gap-4">
            <p className="text-sm text-ink-muted">
              Removing this connection deletes its {children.length} destination(s)
              and revokes every key issued under them. This can't be undone.
            </p>
            <button
              className="btn-danger shrink-0"
              disabled={remove.isPending}
              onClick={() => {
                if (
                  confirm(
                    `Remove connection "${c.displayName}"? This deletes its destinations and keys.`,
                  )
                )
                  remove.mutate();
              }}
            >
              {remove.isPending ? "Removing…" : "Remove connection"}
            </button>
          </div>
        </div>
      )}

      <AddDestinationModal
        open={adding}
        onClose={() => setAdding(false)}
        connectors={connector.data ? [connector.data] : []}
        fixedConnectorId={id}
        onCreated={() => destinations.refetch()}
      />
    </div>
  );
}

function NameEditor({
  id,
  name,
  editable,
  onSaved,
}: {
  id: string;
  name: string;
  editable: boolean;
  onSaved: () => void;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(name);

  const save = useMutation({
    mutationFn: () => api.connectors.update(id, { displayName: value.trim() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["connectors"] });
      setEditing(false);
      onSaved();
    },
  });

  if (!editable) {
    return <h1 className="text-xl font-semibold text-ink">{name}</h1>;
  }

  if (!editing) {
    return (
      <h1 className="group flex items-center gap-2 text-xl font-semibold text-ink">
        {name}
        <button
          className="text-ink-subtle opacity-0 transition-opacity hover:text-ink group-hover:opacity-100"
          onClick={() => {
            setValue(name);
            setEditing(true);
          }}
          title="Rename"
          aria-label="Rename"
        >
          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
          </svg>
        </button>
      </h1>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <input
        className="input max-w-xs"
        value={value}
        autoFocus
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && value.trim()) save.mutate();
          if (e.key === "Escape") setEditing(false);
        }}
      />
      <button
        className="btn-primary"
        disabled={!value.trim() || save.isPending}
        onClick={() => save.mutate()}
      >
        {save.isPending ? "Saving…" : "Save"}
      </button>
      <button className="btn-secondary" onClick={() => setEditing(false)}>
        Cancel
      </button>
    </div>
  );
}

/** Access mode toggle + member picker (managers only). Switching to "Select
 *  members" reveals the org directory with the assigned set pre-ticked. */
function AccessCard({
  connectorId,
  accessMode,
}: {
  connectorId: string;
  accessMode: AccessMode;
}) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<AccessMode>(accessMode);

  const saveMode = useMutation({
    mutationFn: (m: AccessMode) =>
      api.connectors.update(connectorId, { accessMode: m }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["connector", connectorId] });
      qc.invalidateQueries({ queryKey: ["connectors"] });
    },
  });

  const orgMembers = useQuery({
    queryKey: ["orgMembers"],
    queryFn: () => api.org.members(),
    enabled: mode === "members",
  });
  const assigned = useQuery({
    queryKey: ["connectorMembers", connectorId],
    queryFn: () => api.connectors.members(connectorId),
    enabled: mode === "members",
  });

  const [selected, setSelected] = useState<Set<string> | null>(null);
  useEffect(() => {
    if (assigned.data) setSelected(new Set(assigned.data.map((m) => m.userId)));
  }, [assigned.data]);

  const saveMembers = useMutation({
    mutationFn: () =>
      api.connectors.setMembers(connectorId, {
        members: [...(selected ?? [])].map((userId) => ({
          userId,
          email: orgMembers.data?.find((m) => m.userId === userId)?.email ?? null,
        })),
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["connectorMembers", connectorId] }),
  });

  function choose(m: AccessMode) {
    setMode(m);
    saveMode.mutate(m);
  }

  const assignedIds = new Set((assigned.data ?? []).map((m) => m.userId));
  const dirty =
    selected != null &&
    (selected.size !== assignedIds.size ||
      [...selected].some((id) => !assignedIds.has(id)));

  return (
    <div className="card p-5">
      <h2 className="mb-1 text-sm font-semibold text-ink">Access</h2>
      <p className="mb-3 text-xs text-ink-subtle">
        Controls who can see and use this connection and all its destinations.
        People who can't use it never see that it exists.
      </p>

      <div className="inline-flex overflow-hidden rounded-md border border-surface-200 text-sm">
        {(["org", "admins", "members"] as AccessMode[]).map((m) => (
          <button
            key={m}
            type="button"
            className={`px-3 py-1.5 font-medium ${
              mode === m
                ? "bg-accent text-white"
                : "bg-surface-0 text-ink-muted hover:bg-surface-50"
            }`}
            onClick={() => choose(m)}
            disabled={saveMode.isPending}
          >
            {ACCESS_LABEL[m]}
          </button>
        ))}
      </div>

      {mode === "members" && (
        <div className="mt-4">
          {orgMembers.isLoading || assigned.isLoading ? (
            <p className="text-sm text-ink-muted">Loading members…</p>
          ) : orgMembers.isError ? (
            <p className="text-sm text-danger">
              Couldn't load the member list from OpsBelt.
            </p>
          ) : (
            <>
              <div className="divide-y divide-surface-100 rounded-md border border-surface-200">
                {(orgMembers.data ?? []).map((m) => {
                  const on = selected?.has(m.userId) ?? false;
                  return (
                    <label
                      key={m.userId}
                      className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-surface-50"
                    >
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-surface-300 text-accent focus:ring-accent/30"
                        checked={on}
                        onChange={(e) => {
                          setSelected((prev) => {
                            const next = new Set(prev ?? []);
                            if (e.target.checked) next.add(m.userId);
                            else next.delete(m.userId);
                            return next;
                          });
                        }}
                      />
                      <OwnerCell email={m.email} youEmail={undefined} />
                      <span className="ml-auto font-mono text-[10px] uppercase tracking-wide text-ink-subtle">
                        {m.role}
                      </span>
                    </label>
                  );
                })}
              </div>
              {saveMembers.isError && (
                <p className="mt-2 text-sm text-danger">Couldn't save assignments.</p>
              )}
              <div className="mt-3 flex items-center gap-3">
                <button
                  className="btn-primary"
                  disabled={!dirty || saveMembers.isPending}
                  onClick={() => saveMembers.mutate()}
                >
                  {saveMembers.isPending ? "Saving…" : "Save members"}
                </button>
                {saveMembers.isSuccess && !dirty && (
                  <span className="text-sm text-ok">✓ Saved</span>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-ink-subtle">{label}</dt>
      <dd className={`mt-0.5 text-sm text-ink ${mono ? "mono break-all" : ""}`}>
        {value}
      </dd>
    </div>
  );
}
